import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { EmailService } from './email.service';

const ABANDONMENT_HOURS = 24;

// Only reaches carts with a resolvable customerId — guest carts (no logged-in
// user) have no email to send to and are skipped. Runs hourly, but only picks
// up carts that fell into a ~24h-old *window* (not "older than 24h") so the
// same cart isn't re-emailed every hour after it crosses the threshold —
// avoids needing a new "remindedAt" column for this first version.
@Injectable()
export class CartAbandonmentReminderService {
  private readonly logger = new Logger(CartAbandonmentReminderService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly emailService: EmailService
  ) {}

  @Cron('0 * * * *')
  async sendReminders(): Promise<void> {
    const windowEnd = new Date(Date.now() - ABANDONMENT_HOURS * 60 * 60 * 1000);
    const windowStart = new Date(windowEnd.getTime() - 60 * 60 * 1000);

    const carts = await this.prisma.cart.findMany({
      where: {
        status: 'active',
        isActive: true,
        customerId: { not: null },
        updatedAt: { gte: windowStart, lt: windowEnd },
        subtotal: { gt: 0 },
      },
      select: { id: true, storeId: true, customerId: true },
    });

    let sent = 0;
    for (const cart of carts) {
      const user = cart.customerId
        ? await this.prisma.user.findFirst({
            where: { id: cart.customerId },
            select: { name: true, email: true },
          })
        : null;

      if (!user) {
        continue;
      }

      await this.emailService.sendTemplated('cart_abandonment', user.email, {
        customerName: user.name,
        cartUrl: `${process.env.DEFAULT_FRONTEND_ORIGIN ?? 'http://localhost:4200'}/cart`,
      });
      sent += 1;
    }

    if (sent > 0) {
      this.logger.log(`Sent cart abandonment reminder to ${sent} customer(s)`);
    }
  }
}
